/**
 * ThemeToggle — light / dark / system, as one accessible group (design-system PR-1, 2026-09-23).
 *
 * The one control that sets `themePreference` (src/context/AppContext.tsx),
 * which lands on <html data-theme="…"> and is what the semantic --color-*
 * tokens read (src/styles/theme.ts). It is mounted in ONE place for now — the
 * header's account menu (src/components/ProfileImage.tsx) — and only for the
 * developer organisation until PR-6 (D-4, C8: 623 hardcoded hexes would render
 * half-dark today). The gate is the CALLER's: this module is Firebase-free by
 * design so a test can render it without the firebase/auth load-time trap.
 *
 * Structure: a `radiogroup` of three `radio` buttons (roving tabindex, arrow
 * keys move the choice), each at least 44px tall (the touch floor), lowercase
 * labels in the house voice, and a visible ring on :focus-visible drawn with
 * the focus-ring token — the ring today's global outline paints is gold on
 * cream at 2.2:1, under the 3:1 floor. A chip is a pill (the shape rule).
 */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useAppContext } from '../../context/AppContext';
import { THEME_PREFERENCES, type ThemePreference } from '../../styles/themePreference';

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Caption = styled.span`
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--color-ink-3);
  margin-right: 4px;
`;

const Choice = styled.button<{ $on: boolean }>`
  min-height: 44px;
  min-width: 44px;
  padding: 0 12px;
  border-radius: 999px;
  /* The chosen segment is a small satin bar: by day its EDGE is its boundary (PR-1b, D-39);
     gold-lo is now the body's last stop and no longer a boundary. */
  border: 1px solid ${(p) => (p.$on ? 'var(--color-gold-edge)' : 'var(--color-line-control)')};
  background: ${(p) => (p.$on ? 'var(--color-accent)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--color-accent-ink)' : 'var(--color-ink-2)')};
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease;

  &:hover {
    border-color: var(--color-ink);
    color: ${(p) => (p.$on ? 'var(--color-accent-ink)' : 'var(--color-ink)')};
  }

  &:focus,
  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export interface ThemeToggleProps {
  className?: string;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { themePreference, setThemePreference } = useAppContext();

  // Arrow keys move the choice (the radiogroup contract); Home/End jump.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const index = THEME_PREFERENCES.indexOf(themePreference);
      let next: number | null = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % THEME_PREFERENCES.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + THEME_PREFERENCES.length) % THEME_PREFERENCES.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = THEME_PREFERENCES.length - 1;
      if (next === null) return;
      event.preventDefault();
      const value = THEME_PREFERENCES[next];
      setThemePreference(value);
      const target = event.currentTarget.querySelector<HTMLButtonElement>(`[data-theme-choice="${value}"]`);
      target?.focus();
    },
    [themePreference, setThemePreference],
  );

  return (
    <Group
      className={className}
      role="radiogroup"
      aria-label="theme"
      data-theme-toggle=""
      onKeyDown={onKeyDown}
    >
      <Caption aria-hidden="true">theme</Caption>
      {THEME_PREFERENCES.map((value: ThemePreference) => {
        const on = themePreference === value;
        return (
          <Choice
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            data-theme-choice={value}
            $on={on}
            onClick={() => setThemePreference(value)}
          >
            {value}
          </Choice>
        );
      })}
    </Group>
  );
};

export default ThemeToggle;
